import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { identify as pixelIdentify } from "@/lib/pixels";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bf_user") || "null"); } catch { return null; }
  });
  // Loading only when we have a token but need to validate it. Anonymous
  // visitors (no token) → loading stays false → landing renders instantly.
  const [loading, setLoading] = useState(() => {
    try {
      const hasToken = !!localStorage.getItem("bf_token");
      const hasUser = !!localStorage.getItem("bf_user");
      return hasToken && !hasUser;
    } catch { return false; }
  });

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("bf_token");
    if (!token) { setUser(null); setLoading(false); return null; }
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("bf_user", JSON.stringify(data));
      try { if (data?.email || data?.id) pixelIdentify({ email: data.email, external_id: data.id }); } catch {}
      return data;
    } catch {
      localStorage.removeItem("bf_token"); localStorage.removeItem("bf_user");
      setUser(null); return null;
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // Skip entirely on OAuth callback (handled downstream)
    if (window.location.hash?.includes("session_id=")) return;
    // Only hit /auth/me when a token exists AND we are on a route that cares.
    const token = localStorage.getItem("bf_token");
    if (!token) return; // Anonymous visitor on landing → no network at all.
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("bf_token", data.token);
    localStorage.setItem("bf_user", JSON.stringify(data.user));
    setUser(data.user);
    try { pixelIdentify({ email: data.user?.email, external_id: data.user?.id }); } catch {}
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("bf_token", data.token);
    localStorage.setItem("bf_user", JSON.stringify(data.user));
    setUser(data.user);
    try { pixelIdentify({ email: data.user?.email, external_id: data.user?.id }); } catch {}
    return data; // includes {user, verification_sent, dev_verification_url}
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("bf_token"); localStorage.removeItem("bf_user");
    setUser(null);
    window.location.href = "/";
  };

  const setToken = (token, u) => {
    localStorage.setItem("bf_token", token);
    localStorage.setItem("bf_user", JSON.stringify(u));
    setUser(u);
  };

  return <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setToken }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
